import { registerActions } from "../actionRegistry";
import { getAction } from "../actionRegistry";
/**
 * Mockup and Interior Functions
 *
 * Handles mockup selection, filtering, layer adjustments, and interior preview.
 */

import { getDomElement, setText, setStyle, queryAll, idSuffix, h } from "../utils/dom";

/**
 * Paint one mockup tile's selected/unselected state: icon glyph, icon classes and
 * (in the "mockup-new" layout) the tile border.
 */
function paintMockupSelection(raw_id: string, selected: boolean, mockupNew: boolean): void {
  // One lookup, not two: this used to `setHtml` by id and then immediately
  // `getDomElement` the same id back to reach the element's classList.
  const icon = getDomElement(`interior-icon-${raw_id}`);
  if (icon) {
    icon.replaceChildren(h("i", selected ? "fas fa-check-square" : "far fa-square"));
    icon.classList.toggle("interior-select-icon-on", selected);
    icon.classList.toggle("interior-select-icon-off", !selected);
  }
  if (mockupNew) {
    setStyle(`intobject-${raw_id}`, "border", selected ? "5px solid #4AA14F" : "5px solid #fff");
  }
}

/** Every `.interior-select` element paired with its raw mockup id. */
function mockupSelects(): { el: HTMLElement; id: string }[] {
  return Array.from(queryAll<HTMLElement>(".interior-select"), (el) => ({
    el,
    id: idSuffix(el, "interior-"),
  })).filter(({ id }) => id !== "");
}

/**
 * Toggle interior selection state
 * @param id - The interior ID
 */
export function toggleMockup(id: string): void {
  const interiorInput = getDomElement<HTMLInputElement>(`interior-${id}`);
  const mockupNew = getDomElement("mockup-new") != null;

  if (!interiorInput) return;

  const selected = interiorInput.value === "0";
  paintMockupSelection(id, selected, mockupNew);
  interiorInput.value = selected ? "1" : "0";

  if (mockupNew) {
    mockupSelectProcess();
  }
}

/**
 * Deselect all mockups/interiors — delegates to Preact island when active
 */
export function mockupDeselect(): void {
  const deselectAll = getAction("__mockupDeselectAll");
  if (deselectAll) {
    deselectAll();
    return;
  }

  const mockupNew = getDomElement("mockup-new") != null;

  for (const { id } of mockupSelects()) {
    const interiorInput = getDomElement<HTMLInputElement>(`interior-${id}`);
    if (interiorInput?.value !== "1") continue;
    paintMockupSelection(id, false, mockupNew);
    interiorInput.value = "0";
  }

  mockupSelectProcess();
}

/**
 * Count selected mockups and update UI accordingly
 */
export function mockupSelectProcess(): void {
  const mockcount = mockupSelects().filter(
    ({ id }) => getDomElement<HTMLInputElement>(`interior-${id}`)?.value === "1"
  ).length;

  setText("mockup-count", mockcount.toString());

  if (mockcount === 1) {
    setText("mockup-s", "");
  } else {
    setText("mockup-s", "s");
  }

  setStyle("mockup-generate", "opacity", mockcount > 0 ? "1" : "0.2");
}

/**
 * Filter mockup previews — delegates to Preact island when active
 */
export function mockupFilter(): void {
  const setFilter = getAction("__mockupSetFilter");
  if (setFilter) {
    // Read PHP filter selects and push into Preact state
    for (const el of queryAll<HTMLSelectElement>(".mockup-filters")) {
      if (el.id) setFilter(el.id, el.value);
    }

    const selectedFilter = getDomElement<HTMLInputElement>("mockup-filter-selected");
    getAction("__mockupSetSelectedOnly")?.(selectedFilter?.checked ?? false);

    const searchInput = getDomElement<HTMLInputElement>("mockup-search");
    getAction("__mockupSetSearch")?.(searchInput?.value ?? "");
  }

  // There used to be a ~50-line "legacy DOM-based filtering" fallback here for
  // when `__mockupSetFilter` is absent. It could never do anything useful: the
  // tiles it walked (`.interiorpreview-new`) are rendered *only* by
  // `MockupSelectorGrid`, which registers that action in its own render pass —
  // so tiles and action always exist together, and with no grid mounted the
  // fallback iterated an empty set. The per-tile inputs it read
  // (`intobject-`, `mockup-keywords-`, `mockup-name-`) are emitted by nothing at
  // all, so every iteration hit its `continue`.
  //
  // Worth noting what it *would* have done had it ever run: its first loop set
  // `display: none` on every tile, and the `continue` then skipped the loop that
  // shows them again — filtering would have blanked the grid.
}

/**
 * Filter interior previews by category
 */
export function filterMockupCards(): void {
  const filterInput = getDomElement<HTMLInputElement>("interior_filter");

  // Hide all interior previews
  for (const el of queryAll<HTMLElement>(".interiorpreview")) {
    el.style.display = "none";
  }

  // Show only matching category
  if (filterInput?.value) {
    for (const el of queryAll<HTMLElement>(`.interiorpreview-${filterInput.value}`)) {
      el.style.display = "inline-block";
    }
  }
}

/*
 * `toggleMockupFav()` lived here: it called `api.mockups.toggleFav` and then
 * swapped the heart icon by writing `#mockupheart-{id}`.
 *
 * It was dead twice over. Nothing dispatched `mockupfav` — the name appeared
 * only in this file's own `registerActions` — and `mockupheart-*` is emitted by
 * nothing, so even a successful call updated no pixels. `MockupSelectorGrid`'s
 * `toggleFav` (`:132`) is the live implementation: same procedure, but it holds
 * `favs` in state so the heart re-renders from a prop.
 */

registerActions({
  toggleInterior: toggleMockup,
  mockupDeselect,
  mockupSelectProcess,
  interiorfilter: filterMockupCards,
});
